import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowUp, Download, LogIn, Maximize2, Minimize2, Play, Plus, Power, RotateCcw, Save, Square, Trash2, X } from 'lucide-react';
import { useSetAtom } from 'jotai';
import { AudioCreationAuthProvider, useAuth } from '@/auth/AuthProvider';
import { FiloApiClient } from '@/api/FiloApiClient';
import type { SpeechRequest } from '@/api/types';
import { useFiloRemote } from '@/connection/FiloRemoteContext';
import { setFlowMotionAtom } from '@/flow/atoms/flowMotionAtom';
import { nextFlowRequestId } from '@/flow/atoms/flowRequestId';
import { introductionPresentationAtom } from '@/flow/atoms/introductionPresentationAtom';
import { GESTURE_MOTIONS } from '@/flow/config/gestureMotions';
import { SAY_TEXT_VOICES } from '@/flow/config/sayTextVoice';
import { createDetailedIntroduction, createIntroductionNode, executeIntroductionNode, loadIntroduction, loadIntroductionSpeaker, runIntroduction, saveIntroduction, validateIntroduction, type IntroductionNode, type IntroductionState } from '@/speech/introduction';
import { INTRODUCTION_VOICE, loadIntroductionAudio, prepareIntroductionAudio } from '@/speech/introductionAudio';
import { obtainOfflinePreviewAudio } from '@/speech/offlineAudioCache';
import { playSpeechAudio, unlockSpeechAudioContext } from '@/speech/speechAudioContext';

const VirtualRobot = lazy(() => import('@/flow/components/VirtualRobot').then((module) => ({ default: module.VirtualRobot })));

type Props = {
  userId: string;
  onClose: () => void;
  synthesize: (request: SpeechRequest, signal?: AbortSignal) => Promise<ArrayBuffer>;
  signIn: () => Promise<void>;
  authenticated: boolean;
};

export function IntroductionCreation({ userId, onClose }: Pick<Props, 'userId' | 'onClose'>) {
  return <AudioCreationAuthProvider><AuthorizedIntroduction userId={userId} onClose={onClose} /></AudioCreationAuthProvider>;
}

function AuthorizedIntroduction({ userId, onClose }: Pick<Props, 'userId' | 'onClose'>) {
  const auth = useAuth();
  const api = new FiloApiClient(auth.getAccessToken);
  useEffect(() => { sessionStorage.removeItem('filo:audio-creation:return'); }, []);
  return <IntroductionDialog userId={userId} onClose={onClose} authenticated={auth.status === 'authenticated'}
    signIn={auth.signIn} synthesize={(request, signal) => api.synthesizeSpeech(request, signal)} />;
}

export function IntroductionDialog({ userId, onClose, synthesize, signIn, authenticated }: Props) {
  const remote = useFiloRemote();
  const setFlowMotion = useSetAtom(setFlowMotionAtom);
  const setPresentationActive = useSetAtom(introductionPresentationAtom);
  const [initial] = useState(() => {
    try { return { nodes: loadIntroduction(userId), speaker: loadIntroductionSpeaker(userId), error: '' }; }
    catch (error) { return { nodes: [] as IntroductionNode[], speaker: INTRODUCTION_VOICE.speaker, error: String(error) }; }
  });
  const [nodes, setNodes] = useState(initial.nodes);
  const [speaker, setSpeaker] = useState(initial.speaker);
  const [error, setError] = useState(initial.error);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [state, setState] = useState<IntroductionState | null>(null);
  const [availability, setAvailability] = useState<Record<string, boolean>>({});
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [audioRevision, setAudioRevision] = useState(0);
  const [presentationSession, setPresentationSession] = useState(false);
  const [presentationOpen, setPresentationOpen] = useState(false);
  const [caption, setCaption] = useState('');
  const [presentationMotionId, setPresentationMotionId] = useState<number | null>(null);
  const previewController = useRef<AbortController | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const controller = useRef<AbortController | null>(null);
  const busyRef = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    const previous = document.activeElement;
    mounted.current = true;
    dialog.current?.showModal();
    return () => { mounted.current = false; controller.current?.abort(); previewController.current?.abort(); if (previous instanceof HTMLElement) previous.focus(); };
  }, []);

  useEffect(() => {
    if (!presentationSession) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !presentationOpen) return;
      event.preventDefault();
      event.stopPropagation();
      setPresentationOpen(false);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    };
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) setPresentationOpen(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, [presentationOpen, presentationSession]);

  const audioFingerprint = JSON.stringify(nodes.map(({ text }) => text.trim()));
  useEffect(() => {
    const abort = new AbortController();
    const texts: string[] = JSON.parse(audioFingerprint);
    setAvailability({});
    void Promise.all([...new Set(texts.filter(Boolean))].map(async (text) => {
      let ready = false;
      try { await loadIntroductionAudio(userId, text, undefined, speaker); ready = true; } catch { ready = false; }
      if (!abort.signal.aborted) setAvailability((current) => ({ ...current, [text]: ready }));
    }));
    return () => abort.abort();
  }, [audioFingerprint, audioRevision, userId, speaker]);

  const stopPreview = () => { previewController.current?.abort(); previewController.current = null; setPreviewId(null); };
  const playPreview = async (node: IntroductionNode) => {
    if (busyRef.current || remote.snapshot.isRunInProgress) return;
    stopPreview();
    const targets = remote.createRunSnapshot();
    const playOnRobot = targets.robotIds.length > 0;
    const abort = new AbortController();
    previewController.current = abort;
    setPreviewId(node.id); setError('');
    try {
      unlockSpeechAudioContext();
      const audio = await obtainOfflinePreviewAudio(userId, { text: node.text, ...INTRODUCTION_VOICE, speaker },
        () => loadIntroductionAudio(userId, node.text, undefined, speaker), (request, signal) => {
          if (!authenticated) throw new Error('音声作成用のログインが必要です。');
          return synthesize(request, signal);
        }, abort.signal);
      abort.signal.throwIfAborted();
      setAvailability((current) => ({ ...current, [node.text.trim()]: true }));
      if (playOnRobot) {
        if (remote.snapshot.isRunInProgress) throw new Error('ロボットは別の処理を実行中です。');
        remote.setRunInProgress(true, targets);
        try {
          const result = await remote.createBatchService(targets).speak(
            { text: node.text, ...INTRODUCTION_VOICE, speaker },
            { nodeId: node.id, timeoutMs: 120_000, signal: abort.signal, speechAudio: audio },
          );
          const failed = result.results.find(({ status }) => status !== 'fulfilled');
          if (failed || result.results.length !== targets.robotIds.length || !result.results.length) {
            throw new Error(failed?.message ?? 'ロボットの発話完了を確認できませんでした。');
          }
        } finally {
          remote.setRunInProgress(false);
        }
      } else {
        await playSpeechAudio(audio, abort.signal);
      }
    } catch (caught) {
      if (!abort.signal.aborted) setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      if (previewController.current === abort) { previewController.current = null; setPreviewId(null); }
    }
  };

  const update = (id: string, patch: Partial<IntroductionNode>) => {
    if (patch.text !== undefined) stopPreview();
    setNodes((current) => current.map((node) => node.id === id ? { ...node, ...patch } : node));
  };
  const move = (index: number, offset: number) => setNodes((current) => {
    const next = [...current];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    return next;
  });

  const enterPresentation = () => {
    setPresentationOpen(true);
    if (dialog.current && !document.fullscreenElement && typeof dialog.current.requestFullscreen === 'function') {
      void dialog.current.requestFullscreen().catch(() => undefined);
    }
  };

  const stopPresentation = () => {
    setError('');
    setState(null);
    controller.current?.abort(new DOMException('Introduction stopped', 'AbortError'));
    setPresentationOpen(false);
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
  };

  const runVirtualMotion = (motionNo: number, signal: AbortSignal) => new Promise<void>((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      window.clearTimeout(timeout);
      signal.removeEventListener('abort', abort);
    };
    const finish = (error?: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (error) reject(error);
      else resolve();
    };
    const abort = () => {
      finish(signal.reason ?? new DOMException('Introduction stopped', 'AbortError'));
      setFlowMotion(null);
    };
    const timeout = window.setTimeout(() => {
      finish(new Error('VirtualRobotのモーション完了を確認できませんでした。'));
      setFlowMotion(null);
    }, 60_000);
    if (signal.aborted) { abort(); return; }
    signal.addEventListener('abort', abort, { once: true });
    setFlowMotion({
      motionNo,
      requestId: nextFlowRequestId(),
      playBgm: false,
      playNativeAudio: false,
      onComplete: () => { finish(); setFlowMotion(null); },
    });
  });

  const save = async () => {
    if (busyRef.current) return;
    stopPreview();
    busyRef.current = true;
    setBusy(true); setError(''); setMessage('');
    const abort = new AbortController();
    controller.current = abort;
    try {
      const saved = validateIntroduction(nodes);
      saveIntroduction(userId, saved, speaker);
      setNodes(saved);
      setMessage('設定を保存しました。');
      const archive = await prepareIntroductionAudio(userId, saved, (request, signal) => {
        if (!authenticated) throw new Error('音声作成用のログインが必要です。設定は保存されています。');
        return synthesize(request, signal);
      }, abort.signal, (completed, total) => { if (mounted.current) setProgress(`音声 ${completed} / ${total}`); }, undefined, speaker);
      if (archive) {
        const url = URL.createObjectURL(new Blob([new Uint8Array(archive)], { type: 'application/zip' }));
        const anchor = document.createElement('a');
        anchor.href = url; anchor.download = 'filo-introduction-audio.zip';
        document.body.appendChild(anchor); anchor.click(); anchor.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
      if (mounted.current) setMessage(archive ? '設定と音声を保存し、ZIPをダウンロードしました。' : '設定を保存しました。音声の再作成は不要です。');
    } catch (caught) { if (mounted.current) setError(caught instanceof Error ? caught.message : String(caught)); }
    finally { busyRef.current = false; controller.current = null; if (mounted.current) { setBusy(false); setProgress(''); setAudioRevision((current) => current + 1); } }
  };

  const login = async () => {
    try {
      stopPreview();
      saveIntroduction(userId, nodes, speaker);
      sessionStorage.setItem('filo:audio-creation:return', 'introduction');
      await signIn();
    } catch (caught) { setError(String(caught)); }
  };

  const run = async () => {
    if (busyRef.current || remote.snapshot.isRunInProgress) return;
    stopPreview();
    unlockSpeechAudioContext();
    setPresentationSession(true);
    setCaption('');
    setPresentationMotionId(null);
    enterPresentation();
    busyRef.current = true;
    setBusy(true); setError(''); setMessage(''); setState(null);
    const abort = new AbortController();
    controller.current = abort;
    let locked = false;
    try {
      const saved = validateIntroduction(nodes);
      if (!saved.length) throw new Error('ノードを追加してください。');
      const targets = remote.createRunSnapshot();
      remote.setRunInProgress(true, targets);
      setPresentationActive(true);
      locked = true;
      const audio = new Map<string, ArrayBuffer>();
      for (const node of saved) {
        abort.signal.throwIfAborted();
        if (node.text && !audio.has(node.text)) audio.set(node.text, await loadIntroductionAudio(userId, node.text, undefined, speaker));
      }
      if (targets.robotIds.length > 0) {
        const service = remote.createBatchService(targets);
        await runIntroduction(saved, (node, completed) => executeIntroductionNode(
          node,
          service,
          audio.get(node.text),
          (part) => { if (part === 'audio') setCaption(''); completed(part); },
          speaker,
          () => {
            if (node.text) setCaption(node.text);
            if (node.motionId !== null) {
              setPresentationMotionId(node.motionId);
              setFlowMotion({ motionNo: node.motionId, requestId: nextFlowRequestId(), playBgm: false, playNativeAudio: false });
            }
          },
          abort.signal,
        ), abort.signal, (next) => {
          if (mounted.current && !(abort.signal.aborted && next.status === 'error')) setState(next);
        });
      } else {
        await runIntroduction(saved, async (node, completed) => {
          if (!node.text) {
            if (node.motionId !== null) {
              setPresentationMotionId(node.motionId);
              await runVirtualMotion(node.motionId, abort.signal);
              completed('motion');
            }
            return;
          }
          const speechAudio = audio.get(node.text);
          if (!speechAudio) throw new Error('自己紹介の音声が未準備です。');
          let motion: Promise<void> | null = null;
          await playSpeechAudio(speechAudio, abort.signal, () => {
            setCaption(node.text);
            if (node.motionId !== null) {
              setPresentationMotionId(node.motionId);
              motion = runVirtualMotion(node.motionId, abort.signal);
            }
          });
          setCaption('');
          completed('audio');
          if (motion) { await motion; completed('motion'); }
        }, abort.signal, (next) => {
          if (mounted.current && !(abort.signal.aborted && next.status === 'error')) setState(next);
        });
      }
    } catch (caught) {
      if (mounted.current && !abort.signal.aborted) setError(caught instanceof Error ? caught.message : String(caught));
    }
    finally {
      if (locked) remote.setRunInProgress(false);
      setPresentationSession(false);
      setPresentationOpen(false);
      setPresentationActive(false);
      setFlowMotion(null);
      setCaption('');
      setPresentationMotionId(null);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
      busyRef.current = false; controller.current = null;
      if (mounted.current) setBusy(false);
    }
  };

  const restoreInitialValues = () => {
    stopPreview();
    const defaults = createDetailedIntroduction();
    try {
      saveIntroduction(userId, defaults, INTRODUCTION_VOICE.speaker);
      setNodes(defaults);
      setSpeaker(INTRODUCTION_VOICE.speaker);
      setAudioRevision((current) => current + 1);
      setState(null);
      setError('');
      setMessage('初期値を保存しました。音声が未準備の場合は「保存・音声準備」を押してください。');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  const iconButton = 'grid size-9 shrink-0 place-items-center rounded-md hover:bg-slate-100 disabled:opacity-30';
  return createPortal(<dialog ref={dialog} aria-labelledby="introduction-title"
    onCancel={(event) => { event.preventDefault(); if (!busyRef.current) onClose(); }}
    className="fixed inset-0 m-auto w-[min(44rem,calc(100vw-2rem))] max-h-[90dvh] overflow-hidden rounded-lg border border-slate-200 bg-white p-0 text-slate-800 shadow-xl backdrop:bg-black/40">
    <div className="flex max-h-[calc(90dvh-2px)] flex-col">
      <header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-3">
        <h2 id="introduction-title" className="text-lg font-bold">ウィーゴ自己紹介</h2>
        <button type="button" disabled={busy} aria-label="自己紹介を閉じる" title="閉じる" onClick={onClose} className={iconButton}><X size={20} /></button>
      </header>
      <div className="min-h-0 overflow-y-auto px-5 py-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-3">
          <span className="text-sm">音声作成: {authenticated ? 'ログイン済み' : '未ログイン'}</span>
          {!authenticated && <button type="button" disabled={busy} onClick={() => void login()} className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm"><LogIn size={16} />音声作成用のログイン</button>}
        </div>
        <label className="mb-4 block text-sm">音声<select aria-label="自己紹介全体の音声" disabled={busy || remote.snapshot.isRunInProgress} value={speaker}
          onChange={(event) => { stopPreview(); setSpeaker(Number(event.target.value)); setState(null); setError(''); setMessage(''); }}
          className="mt-1 block w-full min-w-0 rounded-md border border-slate-300 px-3 py-2">
          {SAY_TEXT_VOICES.map((voice) => <option key={voice.speaker} value={voice.speaker}>{voice.label}</option>)}
        </select></label>
        <ol aria-label="自己紹介の実行順序" className="m-0 list-none p-0">
          {nodes.map((node, index) => <li key={node.id} data-node-id={node.id} className="min-w-0">
            <div className={`rounded-md border bg-white p-3 ${state?.status === 'running' && state.nodeId === node.id ? 'border-emerald-600 ring-2 ring-emerald-200' : 'border-slate-300'}`}>
              <div className="mb-2 flex items-center gap-1">
                <h3 className="min-w-0 flex-1 text-sm font-semibold">{index + 1}</h3>
                <button type="button" disabled={busy || index === 0} onClick={() => move(index, -1)} aria-label={`ノード ${index + 1}を上へ`} title="上へ" className={iconButton}><ArrowUp size={17} /></button>
                <button type="button" disabled={busy || index === nodes.length - 1} onClick={() => move(index, 1)} aria-label={`ノード ${index + 1}を下へ`} title="下へ" className={iconButton}><ArrowDown size={17} /></button>
                <button type="button" disabled={busy} onClick={() => { if (previewId === node.id) stopPreview(); setNodes((current) => current.filter(({ id }) => id !== node.id)); }} aria-label={`ノード ${index + 1}を削除`} title="削除" className={`${iconButton} text-red-700`}><Trash2 size={17} /></button>
              </div>
              <div className="flex items-end gap-2">
                <label className="min-w-0 flex-1 text-sm">文章<textarea aria-label={`ノード ${index + 1}の文章`} rows={2} maxLength={200} disabled={busy} value={node.text} onChange={(event) => update(node.id, { text: event.target.value })} className="mt-1 block w-full resize-y rounded-md border border-slate-300 px-3 py-2" /></label>
                <button type="button" disabled={busy || (remote.snapshot.isRunInProgress && previewId !== node.id) || !node.text.trim()}
                  aria-label={`ノード ${index + 1}の文章を${previewId === node.id ? '停止' : '再生'}`}
                  title={previewId === node.id ? '停止' : availability[node.text.trim()] ? '保存済み音声を再生' : 'オンラインで音声を取得して再生'}
                  data-audio-state={previewId === node.id ? 'playing' : availability[node.text.trim()] === true ? 'ready' : availability[node.text.trim()] === false ? 'error' : 'checking'}
                  onClick={() => { if (previewId === node.id) stopPreview(); else void playPreview(node); }}
                  className={`grid size-9 shrink-0 place-items-center rounded-md border disabled:opacity-40 ${previewId === node.id ? 'border-sky-600 bg-sky-100 text-sky-800' : availability[node.text.trim()] === true ? 'border-emerald-400 bg-emerald-50 text-emerald-800' : availability[node.text.trim()] === false ? 'border-red-300 bg-red-50 text-red-700' : 'border-slate-200 bg-slate-50 text-slate-400'}`}>
                  {previewId === node.id ? <Square size={16} /> : <Play size={16} />}
                </button>
              </div>
              <label className="mt-3 block text-sm">モーション<select aria-label={`ノード ${index + 1}のモーション`} disabled={busy} value={node.motionId ?? ''} onChange={(event) => update(node.id, { motionId: event.target.value === '' ? null : Number(event.target.value) })} className="mt-1 block w-full min-w-0 rounded-md border border-slate-300 px-3 py-2">
                <option value="">なし</option>{GESTURE_MOTIONS.map((motion) => <option key={motion.id} value={motion.id}>{motion.label}</option>)}
              </select></label>
            </div>
            <div className="flex flex-col items-center gap-1 py-2">
              <label className="flex flex-wrap items-center justify-center gap-2 text-xs text-slate-600">待機（秒）<input type="number" aria-label={`ノード ${index + 1}の待機時間`} min={0} max={3600} step={0.1} disabled={busy} value={Number.isNaN(node.delaySeconds) ? '' : node.delaySeconds} onChange={(event) => update(node.id, { delaySeconds: event.target.valueAsNumber })} className="w-24 rounded-md border border-slate-300 px-2 py-1" /></label>
              {index < nodes.length - 1 && <ArrowDown aria-hidden="true" size={22} className="text-emerald-700" />}
            </div>
          </li>)}
        </ol>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={() => setNodes((current) => [...current, createIntroductionNode()])} className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm"><Plus size={16} />ノードを追加</button>
          <button type="button" disabled={busy} onClick={restoreInitialValues} className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm"><RotateCcw size={16} />初期値に戻す</button>
        </div>
        {error && <p role="alert" className="mt-3 break-words text-sm text-red-700">{error}</p>}
        {message && <p role="status" className="mt-3 text-sm text-emerald-800">{message}</p>}
        {progress && <p role="status" className="mt-3 text-sm">{progress}</p>}
        {state && <p role="status" className="mt-3 break-words text-sm">{state.status === 'completed' ? '自己紹介が完了しました。' : state.status === 'error' ? state.message : `実行中: ${nodes.findIndex(({ id }) => id === state.nodeId) + 1} / ${nodes.length}`} 音声: {state.audio} / モーション: {state.motion}</p>}
      </div>
      <footer className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-200 px-5 py-3">
        <button type="button" disabled={busy} onClick={() => void save()} className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm disabled:opacity-50"><Save size={16} /><Download size={16} />保存・音声準備</button>
        <button type="button" disabled={busy || remote.snapshot.isRunInProgress || !nodes.length} onClick={() => void run()} className="flex items-center gap-2 rounded-md bg-emerald-700 px-3 py-2 text-sm text-white disabled:opacity-50"><Play size={16} />自己紹介を開始</button>
      </footer>
      {presentationSession && <div data-testid="introduction-presentation" className={`introduction-presentation${presentationOpen ? '' : ' is-dismissed'}`} aria-hidden={!presentationOpen}>
        <header className="introduction-presentation__header">
          <strong>ウィーゴ自己紹介</strong>
          <button type="button" aria-label="全画面を閉じる" title="全画面を閉じる" onClick={() => {
            setPresentationOpen(false);
            if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
          }}><Minimize2 aria-hidden="true" size={20} /></button>
        </header>
        <div className="introduction-presentation__stage">
          <div className="introduction-presentation__robot" data-motion-id={presentationMotionId ?? undefined}>
            <Suspense fallback={<div className="introduction-presentation__loading">VirtualRobotを準備中</div>}>
              <VirtualRobot presentationMode />
            </Suspense>
          </div>
          <section className="introduction-presentation__caption" aria-label="字幕" aria-live="polite" aria-atomic="true">
            <span>ウィーゴ</span>
            <p>{caption || (state?.status === 'running' ? '動作中' : '自己紹介を準備しています')}</p>
          </section>
        </div>
      </div>}
      {presentationSession && !presentationOpen && <section className="introduction-presentation-choice" role="dialog" aria-modal="true" aria-label="自己紹介の表示方法">
        <h2>自己紹介は続いています</h2>
        <p aria-live="polite">{caption || '全画面表示を閉じました。'}</p>
        <div>
          <button type="button" onClick={enterPresentation}><Maximize2 aria-hidden="true" size={18} />全画面に戻る</button>
          <button type="button" onClick={stopPresentation}><Power aria-hidden="true" size={18} />自己紹介を終了</button>
        </div>
      </section>}
    </div>
  </dialog>, document.body);
}